/**
 * Print stock labels for a GRV onto 40 × 30 mm thermal stickers.
 *
 * One sticker is printed per UNIT received (a GRV line for qty 40 prints 40
 * stickers). Each sticker shows product name (uppercase, 2-line cap), CODE128
 * barcode, PLU code, and selling price.
 *
 * Uses the same in-page @media print host pattern as `ShelfTalkerModal` —
 * popups and iframes both trigger "Print Failed" in Edge PWA / standalone-app
 * mode so we render directly into the active document.
 *
 * Falls back to the PLU code as the barcode value for products without a
 * real barcode — the till's PLU-priority search will still scan them.
 *
 * @param {Object} grv         GRV object (must have `items` array).
 * @param {Function} toastApi  Sonner toast object (passed in to keep this
 *                             file framework-light).
 */
import JsBarcode from "jsbarcode";
import axios from "axios";

const PRINT_HOST_ID = "bh-stock-label-print-host";
const PRINT_STYLE_ID = "bh-stock-label-print-style";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Cache fetched company settings so we don't re-fetch for every label print.
let _companyCache = null;
let _companyCachePromise = null;
const fetchCompanySettings = async () => {
  if (_companyCache) return _companyCache;
  if (_companyCachePromise) return _companyCachePromise;
  _companyCachePromise = axios
    .get(`${API}/settings/company`)
    .then((r) => {
      _companyCache = {
        shortName: (r.data?.short_name || "BBH").toString(),
        offsetX: Number.isFinite(r.data?.sticker_offset_x_mm) ? Number(r.data.sticker_offset_x_mm) : 0,
        offsetY: Number.isFinite(r.data?.sticker_offset_y_mm) ? Number(r.data.sticker_offset_y_mm) : 0,
      };
      return _companyCache;
    })
    .catch(() => {
      _companyCache = { shortName: "BBH", offsetX: 0, offsetY: 0 };
      return _companyCache;
    });
  return _companyCachePromise;
};

// Physical label is 40×30mm; the printer has a ~2.5mm horizontal × 1.5mm
// vertical dead margin all around, leaving a 35×27mm SAFE printable area.
// (Bumped from 1mm/0.5mm — with only 1mm of slack, even a small
// calibration offset would push the "P" in "PLU" or the bin number on
// the right past the printer's physical edge and get clipped.)
// We render inside that safe area with fixed padding and shift it on the
// physical sticker via CSS transform (translate). Padding-based tricks
// don't actually move printed pixels — flex re-fills the resized box —
// so calibration seemed to do nothing beyond ±1mm.
const SAFE_PADDING = { top: 1.5, right: 2.5, bottom: 1.5, left: 2.5 };
const safePadding = (_offsetX = 0, _offsetY = 0) => SAFE_PADDING;
const shiftTransform = (offsetX = 0, offsetY = 0) =>
  `translate(${offsetX}mm, ${offsetY}mm)`;

const escHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const barcodeDataUrl = (code) => {
  const value = String(code || "").trim();
  if (!value) return null;
  // Always use CODE128 — even for 12/13-digit numbers — so the digits
  // print as one continuous string under the bars (e.g. "2238198315707")
  // instead of EAN-13's gapped "2 23819 83157 7" which the cashier finds
  // confusing on the small sticker. Scanners read both formats identically
  // because the encoded value is the same.
  const tryRender = (fmt) => {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, value, {
      format: fmt,
      width: 2.4,
      height: 70,
      displayValue: true,
      fontSize: 22,
      margin: 2,
      textMargin: 2,
      font: "Arial",
      fontOptions: "bold",
      background: "#ffffff",
      lineColor: "#000000",
    });
    return canvas.toDataURL("image/png");
  };
  try {
    return tryRender("CODE128");
  } catch (_) {
    return null;
  }
};

export const buildStockLabelsHtmlForGRV = async (grv) => {
  if (!grv) return null;
  const company = await fetchCompanySettings();
  const items = (grv.items || []).filter((it) => {
    // Every caller of this function is an explicit user-initiated "print
    // labels now" action (post-GRV modal, GRV History reprint) — so we do
    // NOT filter on the per-row `print_stock_labels` tickbox. That tickbox
    // was only ever an "auto-print on save" hint; if the user clicks Print
    // manually, they want labels for everything received.
    // The GRV form initialises `stock_labels_qty` as an EMPTY STRING —
    // Number("") === 0 (not NaN) — so treat blank/null/undefined as
    // "no override" and fall back to quantity_received.
    const raw = it.stock_labels_qty;
    const rawIsBlank = raw === "" || raw === null || raw === undefined;
    const override = rawIsBlank ? NaN : Number(raw);
    const fallback = Number(it.quantity_received || 0);
    const effective = Number.isFinite(override) && override >= 0 ? override : fallback;
    return effective > 0;
  });
  if (items.length === 0) return null;

  const labels = [];
  items.forEach((it) => {
    const raw = it.stock_labels_qty;
    const rawIsBlank = raw === "" || raw === null || raw === undefined;
    const override = rawIsBlank ? NaN : Number(raw);
    const fallback = Number(it.quantity_received || 0);
    const effective = Number.isFinite(override) && override >= 0 ? override : fallback;
    const copies = Math.max(0, Math.floor(effective));
    if (copies <= 0) return;
    const name = it.product_name || it.name || it.description || "";
    const plu = it.plu_code || "";
    const barcodeValue = (it.barcode && String(it.barcode).trim()) || plu || "";
    const price = Number(it.new_selling_price ?? it.selling_price ?? it.retail_price ?? 0);
    const barcodeImg = barcodeDataUrl(barcodeValue);
    for (let i = 0; i < copies; i++) labels.push({ name, plu, barcodeImg, price });
  });
  if (labels.length === 0) return null;

  const shortName = company.shortName || "BBH";
  const labelsHtml = labels
    .map(
      (lbl) => `
      <div class="bh-stock-label">
        <div class="row1">
          <span class="logo">${escHtml(shortName)}</span>
          <span class="price">R${lbl.price.toFixed(2)}</span>
        </div>
        <div class="row2">
          <span class="plu">PLU ${escHtml(lbl.plu || "—")}</span>
        </div>
        <div class="name">${escHtml(lbl.name)}</div>
        ${lbl.barcodeImg
          ? `<img class="bc" src="${lbl.barcodeImg}" alt="barcode" />`
          : `<div class="bc-text">${escHtml(lbl.plu || "NO-CODE")}</div>`}
      </div>`
    )
    .join("");

  const pad = safePadding(company.offsetX, company.offsetY);
  const shift = shiftTransform(company.offsetX, company.offsetY);

  return `<!doctype html><html><head><meta charset="utf-8" /><title>Stock Labels — ${escHtml(grv.grv_number || "")}</title>
<style>
@page { size: 40mm 30mm; margin: 0; }
html, body { background: #fff; margin: 0; padding: 0; font-family: Arial, sans-serif; color: #000; }
.bh-stock-label {
  width: 40mm; height: 30mm;
  padding: ${pad.top}mm ${pad.right}mm ${pad.bottom}mm ${pad.left}mm;
  transform: ${shift}; transform-origin: top left;
  box-sizing: border-box;
  display: flex; flex-direction: column;
  page-break-after: always;
  overflow: hidden;
}
.bh-stock-label:last-child { page-break-after: auto; }
.row1 { display: flex; justify-content: space-between; align-items: center; }
.row1 .logo { background: #000; color: #fff; font-weight: 900; font-size: 9pt; padding: 0.3mm 1.5mm; letter-spacing: 0.3mm; }
.row1 .price { font-size: 12pt; font-weight: 900; }
.row2 { display: flex; justify-content: space-between; align-items: baseline; font-size: 8pt; font-weight: bold; margin-top: 0.2mm; }
.row2 .plu { font-family: 'Courier New', monospace; }
.name { font-size: 8pt; font-weight: bold; text-transform: uppercase; line-height: 1.05; max-height: 8.5mm; overflow: hidden;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; margin: 0.3mm 0; text-align: center; word-break: break-word; }
.bc { width: 32mm; height: auto; max-height: 8mm; object-fit: contain; margin: 0 auto; display: block; }
.bc-text { font-family: 'Courier New', monospace; font-size: 9pt; text-align: center; padding: 1mm; border: 1px solid #000; }
* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
</style></head><body>${labelsHtml}</body></html>`;
};

export const printStockLabelsForGRV = async (grv, toastApi) => {
  if (!grv) return;
  const company = await fetchCompanySettings();
  const shortName = company.shortName || "BBH";
  const pad = safePadding(company.offsetX, company.offsetY);
  const shift = shiftTransform(company.offsetX, company.offsetY);

  const items = (grv.items || []).filter((it) => {
    // Every caller of this function is an explicit user-initiated "print
    // labels now" action, so we do NOT gate on the per-row
    // `print_stock_labels` tickbox — that was only ever an "auto-print
    // on save" hint. If the user clicks Print manually they want labels
    // for everything received.
    //
    // The GRV form initialises `stock_labels_qty` as an EMPTY STRING —
    // `Number("") === 0` (not NaN) — so a naive
    // `Number.isFinite(override)` check treated every un-touched row as
    // an explicit "print zero labels" and filtered it out, which is
    // exactly the "No received items on this GRV to print labels for"
    // bug reported after a fresh GRV save. Treat only a NON-BLANK
    // numeric value as a real override; blank/null/undefined falls back
    // to `quantity_received`.
    const raw = it.stock_labels_qty;
    const rawIsBlank = raw === "" || raw === null || raw === undefined;
    const override = rawIsBlank ? NaN : Number(raw);
    const fallback = Number(it.quantity_received || 0);
    const effective = Number.isFinite(override) && override >= 0 ? override : fallback;
    return effective > 0;
  });
  if (items.length === 0) {
    toastApi?.warning?.("No received items on this GRV to print labels for.");
    return;
  }

  // Build label list — honour `stock_labels_qty` override per line, else 1 per unit received.
  const labels = [];
  items.forEach((it) => {
    const raw = it.stock_labels_qty;
    const rawIsBlank = raw === "" || raw === null || raw === undefined;
    const override = rawIsBlank ? NaN : Number(raw);
    const fallback = Number(it.quantity_received || 0);
    const effective = Number.isFinite(override) && override >= 0 ? override : fallback;
    const copies = Math.max(0, Math.floor(effective));
    if (copies <= 0) return;
    const name = it.product_name || it.name || it.description || "";
    const plu = it.plu_code || "";
    const barcodeValue =
      (it.barcode && String(it.barcode).trim()) || plu || "";
    const price = Number(
      it.new_selling_price ?? it.selling_price ?? it.retail_price ?? 0
    );
    const barcodeImg = barcodeDataUrl(barcodeValue);
    for (let i = 0; i < copies; i++) {
      labels.push({ name, plu, barcodeImg, price });
    }
  });

  if (labels.length === 0) {
    toastApi?.warning?.("Nothing to print — all received quantities are zero or fractional.");
    return;
  }

  const labelsHtml = labels
    .map(
      (lbl) => `
      <div class="bh-stock-label">
        <div class="row1">
          <span class="logo">${escHtml(shortName)}</span>
          <span class="price">R${lbl.price.toFixed(2)}</span>
        </div>
        <div class="row2">
          <span class="plu">PLU ${escHtml(lbl.plu || "—")}</span>
        </div>
        <div class="name">${escHtml(lbl.name)}</div>
        ${
          lbl.barcodeImg
            ? `<img class="bc" src="${lbl.barcodeImg}" alt="barcode" />`
            : `<div class="bc-text">${escHtml(lbl.plu || "NO-CODE")}</div>`
        }
      </div>`
    )
    .join("");

  // Clean up any leftover host/style from a prior print.
  document.getElementById(PRINT_HOST_ID)?.remove();
  document.getElementById(PRINT_STYLE_ID)?.remove();

  const host = document.createElement("div");
  host.id = PRINT_HOST_ID;
  host.innerHTML = labelsHtml;
  document.body.appendChild(host);

  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    #${PRINT_HOST_ID} { display: none; }

    @media print {
      @page { size: 40mm 30mm; margin: 0; }
      /* Belt-and-braces isolation — same pattern as ShelfTalkerModal. */
      html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
      body * { visibility: hidden !important; }
      body > *:not(#${PRINT_HOST_ID}) { display: none !important; }
      #emergent-badge,
      [id="emergent-badge"],
      [class*="emergent-badge"] { display: none !important; visibility: hidden !important; }
      #${PRINT_HOST_ID},
      #${PRINT_HOST_ID} * { visibility: visible !important; }
      /* No position:fixed — would break page-break-after pagination on multi-label prints. */
      #${PRINT_HOST_ID} {
        display: block !important;
        position: static !important;
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
      }

      #${PRINT_HOST_ID} .bh-stock-label {
        width: 40mm; height: 30mm;
        /* Safe printable area = 38mm × 29mm (printer has 1mm × 0.5mm dead margin). */
        padding: ${pad.top}mm ${pad.right}mm ${pad.bottom}mm ${pad.left}mm;
        transform: ${shift}; transform-origin: top left;
        box-sizing: border-box;
        display: flex; flex-direction: column;
        page-break-after: always;
        font-family: Arial, sans-serif;
        color: #000;
        overflow: hidden;
      }
      #${PRINT_HOST_ID} .bh-stock-label:last-child { page-break-after: auto; }
      #${PRINT_HOST_ID} .row1 {
        display: flex; justify-content: space-between; align-items: center;
      }
      #${PRINT_HOST_ID} .row1 .logo {
        background: #000; color: #fff; font-weight: 900; font-size: 9pt;
        padding: 0.3mm 1.5mm; letter-spacing: 0.3mm;
      }
      #${PRINT_HOST_ID} .row1 .price { font-size: 12pt; font-weight: 900; }
      #${PRINT_HOST_ID} .row2 {
        display: flex; justify-content: space-between; align-items: baseline;
        font-size: 8pt; font-weight: bold; margin-top: 0.2mm;
      }
      #${PRINT_HOST_ID} .row2 .plu { font-family: 'Courier New', monospace; }
      #${PRINT_HOST_ID} .name {
        font-size: 8pt; font-weight: bold; text-transform: uppercase;
        line-height: 1.05; text-align: center;
        max-height: 8.5mm; overflow: hidden;
        display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
        margin: 0.3mm 0; word-break: break-word;
      }
      #${PRINT_HOST_ID} .bc {
        width: 32mm; height: auto; max-height: 8mm; object-fit: contain;
        margin: 0 auto; display: block;
      }
      #${PRINT_HOST_ID} .bc-text {
        font-family: 'Courier New', monospace; font-size: 9pt;
        text-align: center; padding: 1mm; border: 1px solid #000;
      }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    }
  `;
  document.head.appendChild(style);

  const cleanup = () => {
    try { host.remove(); } catch (_) {}
    try { style.remove(); } catch (_) {}
    try {
      const badge = document.getElementById("emergent-badge");
      if (badge && badge.dataset.bhPrintOldDisplay !== undefined) {
        badge.style.display = badge.dataset.bhPrintOldDisplay;
        delete badge.dataset.bhPrintOldDisplay;
      }
    } catch (_) {}
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);

  // Force-hide the Emergent badge during print.
  try {
    const badge = document.getElementById("emergent-badge");
    if (badge) {
      badge.dataset.bhPrintOldDisplay = badge.style.display || "";
      badge.style.display = "none";
    }
  } catch (_) {}

  // Barcode imgs are already data URLs (synchronous) but give the browser a
  // tick to attach them before triggering print.
  setTimeout(() => {
    try {
      window.print();
    } catch (e) {
      console.error("window.print() error:", e);
      toastApi?.error?.("Print failed. Please try again.");
      cleanup();
      return;
    }
    // Safety net for browsers that don't fire afterprint reliably.
    setTimeout(cleanup, 4000);
  }, 80);

  toastApi?.success?.(
    `Printing ${labels.length} stock label${labels.length !== 1 ? "s" : ""}…`
  );
};
